import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { HLSPlayer } from '../components/HLSPlayer';
import {
  getVideo,
  getComments,
  createComment,
  rateVideo,
  getUserRating,
  logWatch,
  incrementView,
  addToWatchlist,
  getStreamUrl,
  likeVideo,
  unlikeVideo,
  checkIfLiked,
  getVideoCast
} from '../api';
import { useAuth } from '../contexts/AuthContext';
import { Star, Eye, Heart, Share2, ChevronLeft, Send, ThumbsUp } from 'lucide-react';
import { toast } from 'sonner';

export const VideoDetailPage = () => {
  const { videoId } = useParams();
  const { user } = useAuth();
  const [video, setVideo] = useState(null);
  const [comments, setComments] = useState([]);
  const [cast, setCast] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [userRating, setUserRating] = useState(null);
  const [liked, setLiked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionId] = useState(() => Math.random().toString(36));
  const [lastPosition, setLastPosition] = useState(0);

  useEffect(() => {
    fetchVideo();
    fetchComments();
    fetchCast();
    if (user) {
      fetchUserRating();
      checkLikeStatus();
      incrementView(videoId).catch(console.error);
    }
  }, [videoId]);

  const fetchVideo = async () => {
    try {
      const response = await getVideo(videoId);
      setVideo(response.data);
    } catch (error) {
      console.error('Failed to fetch video:', error);
      toast.error('Vidéo non trouvée');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await getComments(videoId);
      setComments(response.data.comments);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const fetchCast = async () => {
    try {
      const response = await getVideoCast(videoId);
      setCast(response.data.cast);
    } catch (error) {
      console.error('Failed to fetch cast:', error);
    }
  };


  const fetchUserRating = async () => {
    try {
      const response = await getUserRating(videoId);
      setUserRating(response.data.stars);
    } catch (error) {
      console.error('Failed to fetch rating:', error);
    }
  };

  const checkLikeStatus = async () => {
    try {
      const response = await checkIfLiked(videoId);
      setLiked(response.data.liked);
    } catch (error) {
      console.error('Failed to check like status:', error);
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.error('Connectez-vous pour liker');
      return;
    }

    try {
      if (liked) {
        await unlikeVideo(videoId);
        setLiked(false);
        toast.success('Like retir\u00e9');
      } else {
        await likeVideo(videoId);
        setLiked(true);
        toast.success('Vid\u00e9o lik\u00e9e');
      }
      fetchVideo(); // Refresh pour mettre \u00e0 jour le compteur
    } catch (error) {
      console.error('Failed to like/unlike:', error);
      toast.error("Erreur lors de l'action");
    }
  };

  const handleTimeUpdate = async (currentTime) => {
    if (!user) return;

    const watchTime = Math.floor(currentTime - lastPosition);
    if (watchTime >= 5) {
      try {
        await logWatch({
          video_id: videoId,
          position: Math.floor(currentTime),
          watch_time_seconds: watchTime,
          session_id: sessionId
        });
        setLastPosition(currentTime);
      } catch (error) {
        console.error('Failed to log watch time:', error);
      }
    }
  };

  const handleRate = async (stars) => {
    if (!user) {
      toast.error('Connectez-vous pour noter');
      return;
    }

    try {
      await rateVideo(videoId, stars);
      setUserRating(stars);
      toast.success('Note enregistrée');
      fetchVideo();
    } catch (error) {
      console.error('Failed to rate:', error);
      toast.error('Erreur lors de la notation');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Connectez-vous pour commenter');
      return;
    }

    try {
      await createComment(videoId, newComment);
      setNewComment('');
      toast.success('Commentaire ajouté');
      fetchComments();
    } catch (error) {
      console.error('Failed to comment:', error);
      toast.error('Erreur lors de l\'ajout du commentaire');
    }
  };

  const handleAddToWatchlist = async () => {
    if (!user) {
      toast.error('Connectez-vous pour ajouter à la watchlist');
      return;
    }

    try {
      await addToWatchlist(videoId);
      toast.success('Ajouté à la watchlist');
    } catch (error) {
      console.error('Failed to add to watchlist:', error);
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success('Lien copié dans le presse-papier');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-12">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-4">
            <div className="aspect-video bg-muted rounded-xl" />
            <div className="h-8 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded w-1/4" />
          </div>
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-12">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-muted-foreground">Vidéo non trouvée</p>
          <Link to="/" className="text-primary hover:underline mt-4 inline-block">
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  const playlistUrl = getStreamUrl(video.master_playlist_path);

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ChevronLeft className="w-4 h-4" />
          Retour
        </Link>

        {/* Player */}
        <div className="mb-6">
          <HLSPlayer src={playlistUrl} onTimeUpdate={handleTimeUpdate} />
        </div>

        {/* Video Info */}
        <div className="mb-8">
          <h1
            className="text-2xl md:text-3xl font-bold mb-3"
            data-testid="video-detail-title"
          >
            {video.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {video.views_count} vues
            </span>
            {video.stars_avg > 0 && (
              <span className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                {video.stars_avg.toFixed(1)} ({video.stars_count} notes)
              </span>
            )}
            <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
              {video.creator_username}
            </span>
          </div>

          {/* Actions */}
          <div
            className="flex flex-wrap gap-3 mb-6"
            data-testid="video-detail-actions"
          >
            <button
              onClick={handleLike}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                liked
                  ? 'bg-accent text-accent-foreground'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              <ThumbsUp className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
              {video.likes_count || 0}
            </button>
            <button
              onClick={handleAddToWatchlist}
              className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors"
            >
              <Heart className="w-4 h-4" />
              Favoris
            </button>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Partager
            </button>
          </div>

          {/* Description */}
          <div className="bg-card border border-border rounded-xl p-4 mb-6">
            <p className="text-sm leading-relaxed whitespace-pre-wrap">
              {video.description}
            </p>
          </div>

          {/* Rating */}
          <div className="mb-6" data-testid="video-detail-rating">
            <h2 className="text-xl font-semibold mb-3">Notez cette vidéo</h2>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => handleRate(star)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-8 h-8 ${
                      star <= (userRating || 0)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-muted-foreground'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Cast & Crew */}
        {cast.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Distribution & \u00c9quipe</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {cast.map((member) => (
                <Link
                  key={member.id}
                  to={`/person/${member.person_id}`}
                  className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
                >
                  <div className="w-full aspect-square mb-3 bg-primary/10 rounded-lg overflow-hidden">
                    {member.profile_photo ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/stream/${member.profile_photo}`}
                        alt={member.person_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-primary">
                        {member.person_name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <h3 className="font-medium text-sm text-foreground mb-1">
                    {member.person_name}
                  </h3>
                  <p className="text-xs text-muted-foreground capitalize">
                    {member.role_type}
                  </p>
                  {member.character_name && (
                    <p className="text-xs text-muted-foreground italic mt-1">
                      {member.character_name}
                    </p>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Comments */}
        <div data-testid="video-detail-comments">
          <h2 className="text-xl font-semibold mb-4">
            Commentaires ({comments.length})
          </h2>

          {/* Comment Form */}
          {user && (
            <form onSubmit={handleComment} className="mb-6">
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Ajouter un commentaire..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="flex-1 px-4 py-2 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </form>
          )}

          {/* Comments List */}
          <div className="space-y-4">
            {comments.map((comment) => (
              <div
                key={comment.id}
                className="bg-card border border-border rounded-lg p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium text-sm">{comment.username}</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(comment.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                <p className="text-sm">{comment.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
